import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import { getDocumentTypeLabel } from '../../utils/documentTypeMap';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import './DocumentApprovalsPage.css';

interface KycDocument {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  documentType: string;
  fileName: string;
  originalFileName: string;
  status: 'pending' | 'approved' | 'rejected';
  uploadedAt: string;
  fileUrl?: string;
}

const DocumentApprovalsPage: React.FC = () => {
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingDocuments();
  }, []);

  const fetchPendingDocuments = async () => {
    try {
      const response = await adminApi.getPendingDocuments();
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (documentId: string) => {
    try {
      await adminApi.approveDocument(documentId);
      setDocuments(documents =>
        documents.map(doc =>
          doc._id === documentId
            ? { ...doc, status: 'approved' as const }
            : doc
        )
      );
      alert('Document approved successfully!');
    } catch (error) {
      console.error('Failed to approve document:', error);
      alert('Failed to approve document. Please try again.');
    }
  };

  const handleReject = async (documentId: string, trimmedReason: string) => {
    try {
      await adminApi.rejectDocument(documentId, trimmedReason);
      setDocuments(documents =>
        documents.map(doc =>
          doc._id === documentId
            ? { ...doc, status: 'rejected' as const }
            : doc
        )
      );
      alert('Document rejected successfully.');
    } catch (error: any) {
      console.error('Failed to reject document:', error);

      // Show specific error message from backend if available
      if (error.response?.data?.message) {
        alert(`Failed to reject document: ${error.response.data.message}`);
      } else if (error.response?.data?.errors) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors;
        if (Array.isArray(validationErrors) && validationErrors.length > 0) {
          alert(`Failed to reject document: ${validationErrors[0].message}`);
        } else {
          alert('Failed to reject document. Please check your input and try again.');
        }
      } else {
        alert('Failed to reject document. Please try again.');
      }
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'status-badge approved';
      case 'rejected':
        return 'status-badge rejected';
      default:
        return 'status-badge pending';
    }
  };

  const selectedDocument = documents.find((document) => document._id === selectedDocumentId);

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="document-approvals-page">
        <div className="page-header">
          <h2>Document Approvals</h2>
          <p>Review and approve user document submissions</p>
        </div>

        <div className="approvals-grid">
          {documents.map((document) => (
            <div key={document._id} className="approval-card">
              <div className="card-header">
                <h3>{document.userId.name}</h3>
                <span className={getStatusBadgeClass(document.status)}>
                  {document.status.toUpperCase()}
                </span>
              </div>

              <div className="user-email">
                <strong>Email:</strong> {document.userId.email}
              </div>

              <div className="document-type">
                <strong>Document Type:</strong> {getDocumentTypeLabel(document.documentType)}
              </div>

              <div className="file-name">
                <strong>File:</strong> {document.originalFileName}
              </div>

              <div className="submission-date">
                <strong>Submitted:</strong> {document.uploadedAt ? new Date(document.uploadedAt).toLocaleDateString() : 'N/A'}
              </div>

              {document.fileUrl && (
                <div className="file-preview">
                  <a href={document.fileUrl} target="_blank" rel="noopener noreferrer" className="view-file-btn">
                    View Document
                  </a>
                </div>
              )}

              {document.status === 'pending' && (
                <div className="card-actions">
                  <button
                    onClick={() => setSelectedDocumentId(document._id)}
                    className="approve-btn"
                  >
                    Review in Drawer
                  </button>
                </div>
              )}

              {document.status !== 'pending' && (
                <div className="decision-made">
                  Decision: {document.status.toUpperCase()}
                </div>
              )}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="loading">
            <p>Loading document approvals...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="no-approvals">
            <p>No document approvals pending at this time.</p>
          </div>
        ) : null}

        <ApprovalWorkflowDrawer
          isOpen={Boolean(selectedDocument)}
          title="Document Approval Decision"
          subtitle="Capture approval decision and rejection rationale in one workflow."
          onClose={() => setSelectedDocumentId(null)}
          contextItems={
            selectedDocument
              ? [
                  { label: 'User', value: selectedDocument.userId.name },
                  { label: 'Email', value: selectedDocument.userId.email },
                  {
                    label: 'Document Type',
                    value: getDocumentTypeLabel(selectedDocument.documentType),
                  },
                  {
                    label: 'Submitted',
                    value: selectedDocument.uploadedAt
                      ? new Date(selectedDocument.uploadedAt).toLocaleDateString()
                      : 'N/A',
                  },
                ]
              : []
          }
          onApprove={
            selectedDocument
              ? () => handleApprove(selectedDocument._id)
              : undefined
          }
          onReject={
            selectedDocument
              ? (reason) => handleReject(selectedDocument._id, reason)
              : undefined
          }
          rejectionLabel="Compliance Notes"
          rejectionPlaceholder="Specify missing or invalid evidence in this document."
        />
      </div>
    </DashboardLayout>
  );
};

export default DocumentApprovalsPage;
